import { useState, useEffect, useRef } from "react";
import { Audio } from "@/entities/Audio";
import {
  Play, Pause, SkipBack, SkipForward, Volume2, VolumeX,
  Music, Search, RefreshCw, Shuffle, Repeat
} from "lucide-react";

const CATEGORIAS = ["Todos", "Salsa", "Cumbia", "Vallenato", "Pop", "Rock", "Reggaeton", "Tropical", "Variedad"];

export default function AudioMP3() {
  const [audios, setAudios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [categoria, setCategoria] = useState("Todos");
  const [busqueda, setBusqueda] = useState("");
  const [indiceActivo, setIndiceActivo] = useState(0);
  const [reproduciendo, setReproduciendo] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [duracionActual, setDuracionActual] = useState(0);
  const [duracionTotal, setDuracionTotal] = useState(0);
  const [volumen, setVolumen] = useState(0.8);
  const [silenciado, setSilenciado] = useState(false);
  const [repetir, setRepetir] = useState(false);
  const [aleatorio, setAleatorio] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    cargarAudios();
  }, []);

  async function cargarAudios() {
    setCargando(true);
    const data = await Audio.filter({ activo: true }, "orden", 100);
    setAudios(data);
    setCargando(false);
  }

  const audiosFiltrados = audios.filter((a) => {
    const matchCat = categoria === "Todos" || a.categoria === categoria;
    const matchBus =
      a.titulo?.toLowerCase().includes(busqueda.toLowerCase()) ||
      a.artista?.toLowerCase().includes(busqueda.toLowerCase());
    return matchCat && matchBus;
  });

  const audioActivo = audiosFiltrados[indiceActivo];

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = silenciado ? 0 : volumen;
    }
  }, [volumen, silenciado]);

  useEffect(() => {
    if (audioRef.current && audioActivo) {
      audioRef.current.src = audioActivo.url_audio;
      if (reproduciendo) audioRef.current.play();
    }
  }, [indiceActivo, audioActivo]);

  function togglePlay() {
    if (!audioRef.current || !audioActivo) return;
    if (reproduciendo) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setReproduciendo(!reproduciendo);
  }

  function anterior() {
    if (audiosFiltrados.length === 0) return;
    setIndiceActivo((i) => (i - 1 + audiosFiltrados.length) % audiosFiltrados.length);
    setReproduciendo(true);
  }

  function siguiente() {
    if (audiosFiltrados.length === 0) return;
    if (aleatorio) {
      setIndiceActivo(Math.floor(Math.random() * audiosFiltrados.length));
    } else {
      setIndiceActivo((i) => (i + 1) % audiosFiltrados.length);
    }
    setReproduciendo(true);
  }

  function onTimeUpdate() {
    if (audioRef.current) {
      setDuracionActual(audioRef.current.currentTime);
      setDuracionTotal(audioRef.current.duration || 0);
      setProgreso(audioRef.current.duration ? (audioRef.current.currentTime / audioRef.current.duration) * 100 : 0);
    }
  }

  function onEnded() {
    if (repetir && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    } else {
      siguiente();
    }
  }

  function seekTo(e) {
    if (!audioRef.current || !duracionTotal) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    audioRef.current.currentTime = pct * duracionTotal;
  }

  function formatTime(s) {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  }

  function seleccionarAudio(idx) {
    setIndiceActivo(idx);
    setReproduciendo(true);
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="bg-primary text-primary-foreground px-6 py-4 flex items-center gap-3">
        <Music size={24} />
        <div>
          <h1 className="text-xl font-black">Audio MP3</h1>
          <p className="text-xs text-white/70">Tu música favorita</p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row flex-1">
        {/* Lista de canciones */}
        <div className="flex-1 flex flex-col">
          {/* Filtros */}
          <div className="px-4 py-3 bg-secondary border-b border-border">
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Buscar canción o artista..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  className="w-full bg-background text-foreground text-sm pl-8 pr-3 py-2 rounded border border-border focus:outline-none focus:border-primary"
                />
              </div>
            </div>
            <div className="flex gap-1 flex-wrap mt-2">
              {CATEGORIAS.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategoria(cat)}
                  className={`text-xs px-2 py-1 rounded transition-all font-semibold ${
                    categoria === cat
                      ? "bg-primary text-primary-foreground"
                      : "bg-background text-foreground border border-border hover:bg-muted"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Lista */}
          <div className="flex-1 overflow-y-auto">
            {cargando ? (
              <div className="flex items-center justify-center py-20 text-muted-foreground">
                <RefreshCw size={24} className="animate-spin mr-2" />
                Cargando música...
              </div>
            ) : audiosFiltrados.length === 0 ? (
              <div className="text-center py-20 text-muted-foreground">
                <Music size={48} className="mx-auto mb-3 opacity-30" />
                <p>No hay audios disponibles</p>
              </div>
            ) : (
              audiosFiltrados.map((audio, idx) => (
                <div
                  key={audio.id}
                  onClick={() => seleccionarAudio(idx)}
                  className={`flex items-center gap-3 px-4 py-3 border-b border-border cursor-pointer transition-all
                    ${indiceActivo === idx ? "bg-primary/10 border-l-4 border-l-primary" : "hover:bg-muted"}`}
                >
                  <div className="relative flex-shrink-0">
                    {audio.portada ? (
                      <img src={audio.portada} alt={audio.titulo} className="h-12 w-12 rounded object-cover" />
                    ) : (
                      <div className="h-12 w-12 rounded bg-muted flex items-center justify-center">
                        <Music size={18} className="text-muted-foreground" />
                      </div>
                    )}
                    {indiceActivo === idx && reproduciendo && (
                      <div className="absolute inset-0 bg-primary/60 rounded flex items-center justify-center">
                        <div className="flex gap-0.5 items-end h-4">
                          <div className="w-1 bg-white rounded animate-bounce" style={{ height: "60%", animationDelay: "0ms" }} />
                          <div className="w-1 bg-white rounded animate-bounce" style={{ height: "100%", animationDelay: "150ms" }} />
                          <div className="w-1 bg-white rounded animate-bounce" style={{ height: "40%", animationDelay: "300ms" }} />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold text-sm truncate ${indiceActivo === idx ? "text-primary" : "text-foreground"}`}>
                      {audio.titulo}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{audio.artista || "Artista desconocido"}</p>
                    {audio.album && <p className="text-xs text-muted-foreground/60 truncate">{audio.album}</p>}
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <span className="text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded">{audio.categoria}</span>
                    {audio.duracion && <p className="text-xs text-muted-foreground mt-1">{audio.duracion}</p>}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Panel del reproductor */}
        <div className="w-full lg:w-80 bg-gray-900 text-white flex flex-col">
          {/* Portada */}
          <div className="p-6 flex flex-col items-center">
            {audioActivo?.portada ? (
              <img
                src={audioActivo.portada}
                alt={audioActivo.titulo}
                className={`w-40 h-40 rounded-full object-cover shadow-2xl ${reproduciendo ? "animate-spin" : ""}`}
                style={{ animationDuration: "8s" }}
              />
            ) : (
              <div className={`w-40 h-40 rounded-full bg-primary/30 flex items-center justify-center shadow-2xl ${reproduciendo ? "animate-spin" : ""}`}
                style={{ animationDuration: "8s" }}>
                <Music size={48} className="text-primary" />
              </div>
            )}
            <div className="mt-4 text-center">
              <p className="font-bold text-lg leading-tight">{audioActivo?.titulo || "Sin canción"}</p>
              <p className="text-gray-400 text-sm">{audioActivo?.artista || "—"}</p>
              {audioActivo?.album && <p className="text-gray-500 text-xs">{audioActivo.album}</p>}
            </div>
          </div>

          {/* Barra de progreso */}
          <div className="px-6">
            <div
              className="w-full h-2 bg-gray-700 rounded-full cursor-pointer"
              onClick={seekTo}
            >
              <div
                className="h-2 bg-primary rounded-full transition-all"
                style={{ width: `${progreso}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>{formatTime(duracionActual)}</span>
              <span>{formatTime(duracionTotal)}</span>
            </div>
          </div>

          {/* Controles */}
          <div className="px-6 py-4 flex items-center justify-center gap-4">
            <button
              onClick={() => setAleatorio(!aleatorio)}
              className={`transition-colors ${aleatorio ? "text-accent" : "text-gray-500 hover:text-white"}`}
            >
              <Shuffle size={18} />
            </button>
            <button onClick={anterior} className="text-gray-300 hover:text-white transition-colors">
              <SkipBack size={24} />
            </button>
            <button
              onClick={togglePlay}
              className="bg-primary hover:bg-primary/80 text-white rounded-full p-4 transition-all shadow-lg"
            >
              {reproduciendo ? <Pause size={24} /> : <Play size={24} className="fill-white" />}
            </button>
            <button onClick={siguiente} className="text-gray-300 hover:text-white transition-colors">
              <SkipForward size={24} />
            </button>
            <button
              onClick={() => setRepetir(!repetir)}
              className={`transition-colors ${repetir ? "text-accent" : "text-gray-500 hover:text-white"}`}
            >
              <Repeat size={18} />
            </button>
          </div>

          {/* Volumen */}
          <div className="px-6 pb-6 flex items-center gap-3">
            <button onClick={() => setSilenciado(!silenciado)} className="text-gray-400 hover:text-white">
              {silenciado ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={silenciado ? 0 : volumen}
              onChange={(e) => { setVolumen(parseFloat(e.target.value)); setSilenciado(false); }}
              className="flex-1 accent-primary"
            />
          </div>
        </div>
      </div>

      {/* Audio element oculto */}
      <audio
        ref={audioRef}
        onTimeUpdate={onTimeUpdate}
        onEnded={onEnded}
        onPlay={() => setReproduciendo(true)}
        onPause={() => setReproduciendo(false)}
      />
    </div>
  );
}